// PayClear-only: in-app "Create account" (replaces the hosted-UI /signup link).
// signUp (email as username, plus name) -> confirmSignUp with the emailed code -> SRP sign-in.
// On a pool that only allows admin-created users (Olene-Dev), Cognito answers
// NotAuthorizedException "SignUp is not permitted"; we then explain that an
// administrator creates accounts instead of showing an error.
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { IS_DEV, PROD_APP_URL } from "../stage";
import { Turnstile, TURNSTILE_SITE_KEY } from "../components/Turnstile";
import { confirmSignUp, resendSignUpCode, signIn, signUp } from "../auth/olene";
import { APP_NAME, MSG, isSignUpNotPermitted, messageFor, normalizeIdentifier, signUpMessageFor } from "../auth/messages";
import {
  Alert, Field, Notice, OleneAuthCard, OleneBubble, PasswordField, PrimaryButton, TextLink,
} from "../components/OleneAuthCard";

// Dev sites sign in against the real Olene pool, so accounts are only created on the real
// PayClear (olene-platform/docs/signin.md).
export default function SignUp() {
  if (IS_DEV) return <RedirectToProdSignUp />;
  return <SignUpForm />;
}

function RedirectToProdSignUp() {
  useEffect(() => {
    window.location.replace(`${PROD_APP_URL}/signup`);
  }, []);
  return null;
}

function SignUpForm() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const confirmFor = params.get("confirm");

  const [step, setStep] = useState(confirmFor ? "confirm" : "form"); // form | confirm | closed
  const [name, setName] = useState("");
  const [email, setEmail] = useState(confirmFor || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const firstField = useRef(null);
  const captcha = useRef(null);
  const [captchaToken, setCaptchaToken] = useState("");
  const onToken = useCallback((t) => setCaptchaToken(t), []);

  useEffect(() => {
    firstField.current?.focus();
  }, [step]);

  const onCreate = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError("");
    if (password !== confirmPassword) {
      setError(MSG.mismatch);
      return;
    }
    if (TURNSTILE_SITE_KEY && !captchaToken) {
      setError("Please complete the check above the button.");
      return;
    }
    const username = normalizeIdentifier(email);
    setBusy(true);
    try {
      await signUp(username, password, name.trim(), captchaToken);
      setEmail(username);
      setStep("confirm");
    } catch (err) {
      if (isSignUpNotPermitted(err)) setStep("closed");
      else setError(signUpMessageFor(err));
      captcha.current?.reset(); // tokens are single-use
    } finally {
      setBusy(false);
    }
  };

  const onConfirm = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError("");
    setNotice("");
    setBusy(true);
    const username = normalizeIdentifier(email);
    try {
      await confirmSignUp(username, code.trim());
    } catch (err) {
      setError(signUpMessageFor(err));
      setBusy(false);
      return;
    }
    // Arrived from the sign-in page (no password in memory): sign in there.
    if (!password) {
      navigate("/signin", { replace: true, state: { notice: "Your account is active. Sign in to continue." } });
      return;
    }
    try {
      const result = await signIn(username, password);
      if (result.status === "signedIn") {
        auth.setSession(result.session);
        navigate("/", { replace: true });
      } else {
        navigate("/signin", { replace: true });
      }
    } catch (err) {
      setError(messageFor(err));
      setBusy(false);
    }
  };

  const onResend = async () => {
    setError("");
    setNotice("");
    try {
      await resendSignUpCode(normalizeIdentifier(email));
      setNotice("We sent a new code.");
    } catch (err) {
      setError(signUpMessageFor(err));
    }
  };

  const backToSignIn = (
    <p className="text-center text-sm text-gray-600 mt-5">
      Already have an account?{" "}
      <a href="/signin" className="font-semibold text-green-700 hover:text-green-800 hover:underline">Sign in</a>
    </p>
  );

  if (step === "closed") {
    return (
      <OleneAuthCard title="Create your account" footer={backToSignIn}>
        <OleneBubble body="PayClear uses your Olene account, the same one you use for your other Olene apps." />
        <div role="alert" className="text-sm text-gray-700">{MSG.signUpClosed}</div>
      </OleneAuthCard>
    );
  }

  if (step === "confirm") {
    return (
      <OleneAuthCard title="Check your email" footer={backToSignIn}>
        <p className="text-sm text-gray-600 mb-5">
          {confirmFor && !password
            ? "Enter the code we emailed you to activate your account."
            : `We sent a code to ${email}. Enter it to activate your account.`}
        </p>
        <form onSubmit={onConfirm} noValidate>
          <Notice>{notice}</Notice>
          <Alert>{error}</Alert>
          {confirmFor && !password && (
            <Field id="email" label="Email" type="email" autoComplete="username" autoCapitalize="none"
              spellCheck={false} required value={email} onChange={(e) => setEmail(e.target.value)}
              inputRef={firstField} />
          )}
          <Field id="code" label="Code" inputMode="numeric" autoComplete="one-time-code" required
            value={code} onChange={(e) => setCode(e.target.value.trim())}
            inputRef={confirmFor && !password ? undefined : firstField} />
          <PrimaryButton busy={busy} busyLabel="Activating…">Activate account</PrimaryButton>
        </form>
        <div className="mt-4 text-center">
          <TextLink onClick={onResend}>Send a new code</TextLink>
        </div>
      </OleneAuthCard>
    );
  }

  return (
    <OleneAuthCard title="Create your account" footer={backToSignIn}>
      <OleneBubble body={`Your ${APP_NAME} account is an Olene account: use the same email and password in every Olene app.`} />
      <form onSubmit={onCreate} noValidate>
        <Alert>{error}</Alert>
        <Field id="name" label="Full name" autoComplete="name" required
          value={name} onChange={(e) => setName(e.target.value)} inputRef={firstField} />
        <Field id="email" label="Email" type="email" autoComplete="username" autoCapitalize="none"
          spellCheck={false} required value={email} onChange={(e) => setEmail(e.target.value)} />
        <PasswordField id="password" label="Password" autoComplete="new-password" required
          value={password} onChange={(e) => setPassword(e.target.value)} hint={MSG.passwordRules} />
        <PasswordField id="confirm-password" label="Confirm password" autoComplete="new-password" required
          value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
        <Turnstile ref={captcha} onToken={onToken} />
        <PrimaryButton busy={busy} busyLabel="Creating account…">Create account</PrimaryButton>
      </form>
    </OleneAuthCard>
  );
}
